import React, { useState, useRef, useEffect } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { ViewModeSwitcher } from '@/components/navigation/ViewModeSwitcher';
import { UserMenu } from '@/components/auth/UserMenu';
import { Button } from '@/components/ui/button';
import { Sun, Moon, Bug } from 'lucide-react';
import { BugReportDialog } from '@/components/feedback/BugReportDialog';
import { cn } from '@/lib/utils';
import { useDockMagnify } from './useDockMagnify';
import { DockGlyph } from './DockGlyph';

// How long the pill stays expanded after the pointer leaves, so a brief
// overshoot doesn't collapse the view mode labels out from under the cursor.
export const TOPBAR_COLLAPSE_DELAY_MS = 700;

interface TopBarProps {
  scrollOffset?: number;
}

export function TopBar({ scrollOffset = 0 }: TopBarProps) {
  const isDarkMode = useCanvasStore((s) => s.isDarkMode);
  const toggleDarkMode = useCanvasStore((s) => s.toggleDarkMode);
  const viewMode = useCanvasStore((s) => s.viewMode);
  const [showBugReport, setShowBugReport] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement | null>(null);
  // Docked to the top edge, so hovered glyphs rise downward. A small lift: the
  // view mode buttons clip their contents so their labels can slide in.
  const dock = useDockMagnify<HTMLDivElement>({ rise: 'down', lift: 2 });
  const collapseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelCollapse = () => {
    if (collapseTimeoutRef.current) {
      clearTimeout(collapseTimeoutRef.current);
      collapseTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    cancelCollapse();
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    cancelCollapse();
    collapseTimeoutRef.current = setTimeout(() => {
      collapseTimeoutRef.current = null;
      setIsHovered(false);
    }, TOPBAR_COLLAPSE_DELAY_MS);
  };

  useEffect(() => cancelCollapse, []);

  // The profile menu is anchored to the avatar, so the pill must not resize
  // while it is open: every expand or collapse dragged the open menu with it.
  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (open) {
      cancelCollapse();
      setIsHovered(true);
    } else if (!pillRef.current?.matches(':hover')) {
      // Pointer already gone: collapse after the usual delay, not at once
      setIsHovered(true);
      handleMouseLeave();
    }
  };

  // In discover mode, the TopBar moves up as user scrolls
  // Use CSS transition for smooth movement, but clamp values to prevent sticking
  const isDiscover = viewMode === 'discover';
  const clampedOffset = Math.max(0, Math.min(scrollOffset, 100));
  const translateY = isDiscover ? Math.min(clampedOffset * 0.8, 80) : 0;
  const opacity = isDiscover ? Math.max(1 - (clampedOffset / 100), 0) : 1;

  return (
    <div 
      className={cn(
        "fixed left-1/2 z-50 top-5",
        // Use CSS transition for smoother animation
        "transition-[transform,opacity] duration-200 ease-out"
      )}
      style={{ 
        transform: `translateX(-50%) translateY(-${translateY}px)`,
        opacity,
        pointerEvents: opacity < 0.3 ? 'none' : 'auto'
      }}
    >
      {/* Floating Pill Navigation */}
      <div
        ref={(el) => {
          pillRef.current = el;
          dock.ref.current = el;
        }}
        {...dock.handlers}
        className="floating-pill rounded-full px-3 py-2 flex items-center gap-2 max-w-[600px]"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {/* Bug Report */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setShowBugReport(true)}
          title="Report a bug"
          className="h-9 w-9 rounded-full"
          data-dock-item
        >
          <DockGlyph>
            <Bug className="h-4 w-4" />
          </DockGlyph>
        </Button>

        {/* View Mode Switcher */}
        <ViewModeSwitcher expanded={isHovered || isMenuOpen} />

        {/* Theme toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleDarkMode}
          className="h-9 w-9 rounded-full"
          data-dock-item
        >
          <DockGlyph>
            {isDarkMode ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </DockGlyph>
        </Button>

        {/* Separator */}
        <div className="w-px h-6 bg-border/50" />

        {/* User Menu */}
        <UserMenu onOpenChange={handleMenuOpenChange} />
      </div>

      {/* Bug Report Dialog */}
      <BugReportDialog 
        isOpen={showBugReport} 
        onClose={() => setShowBugReport(false)} 
      />
    </div>
  );
}
